// frontend/src/stores/toastStore.ts — first Zustand store.
//
// Creates `frontend/src/stores/` per CONVENTIONS §Module shape ("No empty
// folders at genesis. ... frontend/src/stores/ lands when the first Zustand
// store is authored — toast queue").
//
// Why Zustand and not React Context for our state (per CLAUDE.md NOT-DO +
// ADR-003 §Revocation propagation): Context re-renders every consumer on any
// value change. Zustand lets each consumer subscribe to the exact slice it
// needs — the navbar doesn't re-render when a toast appears in a form.
//
// Per COMPONENTS §Per-primitive WCAG (Toast row): non-critical toasts auto-
// dismiss at ≥5s; errors do NOT auto-dismiss (user must acknowledge). The
// store doesn't manage timers — `ToastProvider` mounts a timer per toast
// when added. Errors are added without a timer.

import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

/** One act the toast offers — "Undo" on a move or a delete, "Open" on a place. It runs, then the toast leaves. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}
export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
  /** A KEYED toast replaces its predecessor with the same key instead of
   *  stacking beside it — the busy hint (ADR-079 §5), said once however many
   *  reads meet it; the replacement starts its own dismissal clock. */
  key?: string;
}

interface ToastState {
  toasts: ToastItem[];
  add: (toast: Omit<ToastItem, 'id'>) => string;
  remove: (id: string) => void;
  clear: () => void;
}

const MAX_TOASTS = 3;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  add: (toast) => {
    const id = crypto.randomUUID();
    set((state) => {
      const kept = toast.key === undefined ? state.toasts : state.toasts.filter((t) => t.key !== toast.key);
      const next = [...kept, { ...toast, id }];
      // Keep only the last MAX_TOASTS — oldest are dropped silently if
      // the user is firing toasts faster than they can read them.
      return { toasts: next.slice(-MAX_TOASTS) };
    });
    return id;
  },
  remove: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));
